import { useEventListener } from 'usehooks-ts';
import MainRouter from "./MainRouter";
import { useExpireAuthData } from '../hooks/auth';
import { ScSidebar } from './ScSidebar';
import { ScHeader } from './ScHeader';
import MainSheets from './MainSheets';
import WarningBar from './WarningBar';
import ConfirmDialog from '@/components/ConfirmDialog';
import PromptDialog from '@/components/PromptDialog';
import TxToaster from '@/components/TxToaster';
import AccountDialog from '@/components/AccountDialog';
import { useOpenAccountModal } from '@/hooks/dialogs';
import PlayerModal from './PlayerModal/PlayerModal';
import { playerModalUrlParam, useOpenPlayerModal } from '@/hooks/playerModal';
import { useLocation } from 'wouter';
import MainSocket from './MainSocket';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useToggleTheme } from '@/hooks/theme';
import { hotkeyEventListener } from '@/lib/hotkeyEventListener';
import BreakpointDebugger from '@/components/BreakpointDebugger';
import ActionModal from './ActionModal/ActionModal';
import { useEffect } from 'react';
import { actionModalUrlParam, useOpenActionModal } from '@/hooks/actionModal';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useFocusMode } from '@/hooks/useFocusMode';
import { PageTransition } from '@/components/PageTransition';
import { TopProgressBar } from '@/components/TopProgressBar/TopProgressBar';
import { CommandPalette } from '@/components/CommandPalette/CommandPalette';
import { ShortcutsOverlay } from '@/components/ShortcutsOverlay';
import { EventToaster } from '@/components/toasts/EventToaster';
import { KonamiMatrix } from '@/components/easter/KonamiMatrix';


export default function MainShell() {
    const expireSession = useExpireAuthData();
    const openAccountModal = useOpenAccountModal();
    const openPlayerModal = useOpenPlayerModal();
    const openActionModal = useOpenActionModal();
    const toggleTheme = useToggleTheme();
    const { focus, toggle } = useFocusMode();
    const [, setLocation] = useLocation();

    //Listener for messages from child iframes (legacy routes) or other sources
    useEventListener('message', (e: TxMessageEvent) => {
        if (e.data.type === 'logoutNotice') {
            expireSession('child iframe', 'got logoutNotice');
        } else if (e.data.type === 'openAccountModal') {
            openAccountModal();
        } else if (e.data.type === 'openPlayerModal') {
            openPlayerModal(e.data.ref);
        } else if (e.data.type === 'navigateToPage') {
            setLocation(e.data.href);
        } else if (e.data.type === 'globalHotkey' && e.data.action === 'toggleLightMode') {
            toggleTheme();
        }
    });

    //auto open the player or action modals
    useEffect(() => {
        const pageUrl = new URL(window.location.toString());
        const playerModalRef = pageUrl.searchParams.get(playerModalUrlParam);
        const actionModalRef = pageUrl.searchParams.get(actionModalUrlParam);
        if (!playerModalRef && !actionModalRef) return;

        if (playerModalRef) {
            if (playerModalRef.includes('#')) {
                const [mutex, rawNetid] = playerModalRef.split('#');
                const netid = parseInt(rawNetid);
                if (mutex.length && rawNetid.length && !isNaN(netid)) {
                    return openPlayerModal({ mutex, netid });
                }
            } else if (playerModalRef.length) {
                return openPlayerModal({ license: playerModalRef });
            }
        } else if (actionModalRef && actionModalRef.length) {
            return openActionModal(actionModalRef);
        }

        //Remove the query params
        pageUrl.searchParams.delete(playerModalUrlParam);
        pageUrl.searchParams.delete(actionModalUrlParam);
        window.history.replaceState({}, '', pageUrl);
    }, []);

    //Listens to hotkeys (doesn't work if the focus is on an iframe)
    useEventListener('keydown', hotkeyEventListener);

    return (
        <TooltipProvider delayDuration={300} disableHoverableContent={true}>
            {/* SC Labs shell: sidebar + main column */}
            <div className="flex h-screen overflow-hidden" style={{ background: 'var(--sc-bg)' }}>
                {!focus && <ScSidebar />}
                <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                    <ScHeader />
                    <main className="flex-1 overflow-y-auto flex flex-col min-h-0" style={{ scrollbarWidth: 'thin' }}>
                        <PageTransition>
                            <MainRouter />
                        </PageTransition>
                    </main>
                </div>
            </div>

            {/* Focus mode toggle */}
            <button
                onClick={toggle}
                title={focus ? 'Exit focus mode (F)' : 'Focus mode (F)'}
                className="fixed bottom-4 left-4 z-[400] grid place-items-center h-9 w-9 rounded-lg sc-glass transition-all hover:brightness-125"
                style={{ border: '1px solid var(--sc-border-mid)', color: 'var(--sc-cyan)' }}
            >
                {focus ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>

            {/* Preserved txAdmin overlays — untouched */}
            <MainSheets />
            <WarningBar />
            <ConfirmDialog />
            <PromptDialog />
            <TxToaster />
            <AccountDialog />
            <PlayerModal />
            <ActionModal />
            <MainSocket />

            {/* Global overlay singletons */}
            <TopProgressBar />
            <CommandPalette />
            <ShortcutsOverlay />
            <EventToaster />
            <KonamiMatrix />
        </TooltipProvider>
    );
}
